import { useStore } from '@app:store';
import { ProductTemplate } from '@app:types';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionItemProps,
  AccordionPanel,
  Button,
  Flex,
  FormLabel,
  Heading,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import { BoxLoader } from 'components/@common/layout';
import React from 'react';
import { RiDeleteBin2Line } from 'react-icons/ri';

export type ProductInputSchema = {
  templateId: string;
  weight: string;
  customizationAttributes: Record<string, string>;
};

export function ProductsInput(props: { value: ProductInputSchema[]; onChange: (value: ProductInputSchema[]) => void }) {
  const [store, dispatch] = useStore();
  const [failedFetch, setFetchFailed] = React.useState(false);

  const handleValueChange = React.useCallback(
    (index: number, value: ProductInputSchema) => {
      const newValue = [...props.value];
      newValue[index] = value;
      props.onChange(newValue);
    },
    [props.value],
  );

  const handleProductCreate = React.useCallback(() => {
    const newValue = [...props.value];
    newValue.push({
      templateId: store.resources.templates.items[0].id,
      weight: store.resources.templates.items[0].specifications.weight.toFixed(2),
      customizationAttributes: {},
    });
    props.onChange(newValue);
  }, [props.value, store.resources.templates.items]);

  const handleProductDelete = React.useCallback(
    (index: number) => {
      if (props.value.length > 1) {
        const newValue = [...props.value];
        newValue.splice(index, 1);
        console.log(newValue);
        props.onChange(newValue);
      }
    },
    [props.value],
  );

  React.useEffect(() => {
    dispatch('resources', 'fetchResources', { resource: 'templates', page: 0, limit: 50 }, undefined, () => setFetchFailed(true));
  }, []);

  React.useEffect(() => {
    if (props.value.length === 0 && store.resources.templates.items.length > 0) {
      handleProductCreate();
    }
  }, [props.value, handleProductCreate, store.resources.templates.items]);

  return (
    <BoxLoader>
      <Accordion allowToggle gap={3}>
        {props.value.map((val, index) => (
          <OrderProductsInputRow
            key={index}
            value={val}
            templateOptions={store.resources.templates.items}
            onChange={handleValueChange.bind(null, index)}
            onDelete={handleProductDelete.bind(null, index)}
            deletedIsDisabled={props.value.length === 1}
            itemProps={{
              mt: index > 0 ? 1 : 0,
            }}
          />
        ))}
        <Button mt={3} w='100%' onClick={handleProductCreate} isLoading={props.value.length === 0}>
          + Add product
        </Button>
      </Accordion>
    </BoxLoader>
  );
}

function OrderProductsInputRow({
  itemProps,
  ...props
}: {
  templateOptions: ProductTemplate[];
  value: ProductInputSchema;
  onChange: (value: ProductInputSchema) => void;
  onDelete: () => void;
  deletedIsDisabled?: boolean;
  itemProps?: AccordionItemProps;
}) {
  const template = React.useMemo<ProductTemplate | undefined>(() => {
    return props.templateOptions.find((t) => t.id === props.value.templateId);
  }, [props.templateOptions, props.value.templateId]);

  const customAttributes = React.useMemo<{ key: string; label: string }[]>(() => {
    if (!template || !template.customizationAttributes) {
      return [{ key: 'expectedShade', label: 'Expected Shade' }];
    }

    return Object.entries(template.customizationAttributes).map(([key, value]) => ({
      key,
      label: value.label,
    }));
  }, [template]);

  const handleChange = React.useCallback(
    (key: keyof ProductInputSchema, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const newValue = JSON.parse(JSON.stringify(props.value)) as ProductInputSchema;
      if (key === 'customizationAttributes') {
        newValue.customizationAttributes[event.target.id] = event.target.value;
      } else {
        newValue[key] = event.target.value;

        if (key === 'templateId') {
          const newTemplate = props.templateOptions.find((t) => t.id === newValue.templateId);
          if (newTemplate) newValue.weight = newTemplate.specifications.weight.toFixed(2);
        }
      }
      props.onChange(newValue);
    },
    [props.onChange, props.templateOptions],
  );

  return (
    <AccordionItem {...itemProps} bgColor='gray.50' border={'0px'}>
      <h2>
        <AccordionButton>
          <Text mr={3} fontWeight={'bold'}>
            {template?.label || 'Unknown'} ({props.value.weight}g)
          </Text>
          <Spacer />
          <AccordionIcon />
          <Button
            variant='outline'
            colorScheme='red'
            p={0}
            size='sm'
            ml={2}
            onClick={props.onDelete}
            isDisabled={!!props.deletedIsDisabled}
          >
            <RiDeleteBin2Line />
          </Button>
        </AccordionButton>
      </h2>
      <AccordionPanel pb={4}>
        <Stack>
          <Flex gap={3} alignItems={'center'}>
            <FormLabel flex={1} margin={0} textAlign={'right'}>
              Template
            </FormLabel>
            <Select flex={4} value={props.value.templateId} onChange={handleChange.bind(null, 'templateId')}>
              {props.templateOptions.map((template) => {
                return (
                  <option key={template.id} value={template.id}>
                    {template.label}
                  </option>
                );
              })}
            </Select>
            <FormLabel flex={1} margin={0} textAlign={'right'}>
              Weight
            </FormLabel>
            <NumberInput
              flex={2}
              value={props.value.weight}
              onChange={(string) => handleChange('weight', { target: { value: string } } as React.ChangeEvent<HTMLInputElement>)}
              step={0.1}
              precision={2}
              min={0}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Select flex={2} isDisabled>
              <option value={'g'}>gram</option>
            </Select>
          </Flex>
          {customAttributes.length > 0 && (
            <Heading as='h3' size='sm' color={'black'} fontWeight={'bold'} mt={4}>
              Customization Attributes
            </Heading>
          )}
          {customAttributes.length > 0 &&
            customAttributes.map(({ key, label }) => {
              return (
                <Flex gap={3} alignItems={'center'}>
                  <FormLabel margin={0} textAlign={'right'}>
                    {label}
                  </FormLabel>
                  <Input
                    key={key}
                    flex={1}
                    id={key}
                    value={props.value.customizationAttributes[key] || ''}
                    onChange={handleChange.bind(null, 'customizationAttributes')}
                  />
                </Flex>
              );
            })}
        </Stack>
      </AccordionPanel>
    </AccordionItem>
  );
}
